import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Search, Filter, Grid, List, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ToolCard } from './ToolCard';

// Mock custom tools data
const mockCustomTools = [{
  name: 'Custom CRM Connector',
  description: 'Connect to your custom CRM system with advanced data synchronization and real-time updates.',
  category: 'CRM',
  connected: true,
  lastUsed: '2 hours ago',
  totalActions: 1247,
  activeActions: 12,
  connectedAccount: 'admin@company.com',
  logoUrl: undefined
}, {
  name: 'Internal API Gateway',
  description: 'Custom API gateway integration for internal microservices communication and data flow.',
  category: 'Productivity',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  connectedAccount: undefined,
  logoUrl: undefined
}, {
  name: 'Marketing Automation Hub',
  description: 'Custom marketing automation platform with advanced campaign management and analytics.',
  category: 'Marketing',
  connected: true,
  lastUsed: '1 day ago',
  totalActions: 856,
  activeActions: 8,
  connectedAccount: 'marketing@company.com',
  logoUrl: undefined
}, {
  name: 'Custom Analytics Dashboard',
  description: 'Proprietary analytics dashboard with custom metrics and real-time business intelligence.',
  category: 'Analytics',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  connectedAccount: undefined,
  logoUrl: undefined
}, {
  name: 'E-commerce Platform API',
  description: 'Custom e-commerce platform integration with inventory management and order processing.',
  category: 'E-commerce',
  connected: true,
  lastUsed: '5 minutes ago',
  totalActions: 2341,
  activeActions: 18,
  connectedAccount: 'ecommerce@company.com',
  logoUrl: undefined
}, {
  name: 'Project Management Suite',
  description: 'Custom project management solution with task automation and team collaboration features.',
  category: 'Project Management',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  connectedAccount: undefined,
  logoUrl: undefined
}];
export const CustomToolsTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [tools, setTools] = useState(mockCustomTools);
  const [showFilters, setShowFilters] = useState(false);
  const categories = ['All', 'CRM', 'Productivity', 'Marketing', 'Analytics', 'E-commerce', 'Project Management'];
  const filteredTools = tools.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) || tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  const connectedCount = tools.filter(tool => tool.connected).length;
  const totalActions = tools.reduce((sum, tool) => sum + tool.totalActions, 0);
  const activeActions = tools.reduce((sum, tool) => sum + tool.activeActions, 0);
  const handleConnect = (toolName: string) => {
    setTools(prev => prev.map(tool => tool.name === toolName ? {
      ...tool,
      connected: true,
      connectedAccount: 'user@company.com',
      lastUsed: 'Just now'
    } : tool));
  };
  const handleDisconnect = (toolName: string) => {
    setTools(prev => prev.map(tool => tool.name === toolName ? {
      ...tool,
      connected: false,
      connectedAccount: undefined,
      lastUsed: 'Never',
      totalActions: 0,
      activeActions: 0
    } : tool));
  };
  return <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Tools</h1>
          <p className="text-gray-600 mt-1">Connect and manage your custom OAuth-based integrations</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Add Custom Tool
        </Button>
      </div>

      {/* Stats Cards */}
      

      {/* Search and Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search custom tools..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <div className="flex border border-gray-200 rounded-md">
            <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('grid')} className="rounded-r-none">
              <Grid className="h-4 w-4" />
            </Button>
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className="rounded-l-none">
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Category Filters */}
      {showFilters && <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
          <div className="flex flex-wrap gap-2">
            {categories.map(category => <Badge key={category} variant={selectedCategory === category ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setSelectedCategory(category)}>
                {category}
              </Badge>)}
          </div>
        </div>}

      {/* Results Count */}
      <div className="text-sm text-gray-600 mb-4">
        Showing {filteredTools.length} of {tools.length} custom tools
      </div>

      {/* Tools Grid/List */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 gap-4' : 'space-y-4'}>
        {filteredTools.map(tool => <ToolCard key={tool.name} tool={tool} onConnect={() => handleConnect(tool.name)} onDisconnect={() => handleDisconnect(tool.name)} />)}
      </div>

      {/* Empty State */}
      {filteredTools.length === 0 && <div className="text-center py-12">
          <div className="text-gray-500 mb-2">No custom tools found</div>
          <div className="text-sm text-gray-400">Try adjusting your search or filters</div>
        </div>}
    </div>;
};